import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for KYC section navigation with smooth scrolling and active section tracking
 * Story 2.2.9: KYC Review & Navigation
 */
export const useKYCNavigation = () => {
  const [activeSection, setActiveSection] = useState<string>('personal');

  /**
   * Scroll to a specific section with smooth animation
   * Accounts for sticky header offset with scroll-mt-6 class
   */
  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Use smooth scroll with offset for sticky elements
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      // Update active section immediately for UI responsiveness
      setActiveSection(sectionId);
    }
  }, []);

  /**
   * Set up Intersection Observer to track which section is currently visible
   * Updates active section as user scrolls
   */
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px', // Trigger when section is in top 30% of viewport
      threshold: 0,
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all KYC section cards
    const sectionIds = [
      'personal',
      'contact',
      'employment',
      'background',
      'tax',
      'pep',
      'wealth',
      'income',
      'documents',
    ];

    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    elements.forEach((element) => observer.observe(element));

    // Cleanup
    return () => {
      elements.forEach((element) => observer.unobserve(element));
    };
  }, []);

  return {
    activeSection,
    scrollToSection,
    setActiveSection, // Allow manual override if needed
  };
};
